import { Grid } from '@mui/material';
import { Links } from 'application/constants/links';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { AppLink } from 'components/uikit/buttons/AppLink';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { Field } from 'components/uikit/Field';
import { FormError } from 'components/uikit/FormError';
import { Input } from 'components/uikit/inputs/Input';
import { Loading } from 'components/uikit/suspense/Loading';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryFactory } from 'services/api';
import { CreateRevisionDto } from 'services/api/api-client';
import { useNavigate } from 'react-router';
import { ProjectProvider } from 'helpers/projectPermissions/ProjectContext';

export const CreateRevisionPage: React.FC = () => {
  const params = Links.Authorized.CreateRevision.useParams();
  const projectId = params.projectId;
  const i18n = useScopedTranslation('Page.Revisions.Create');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const form = useAdvancedForm<CreateRevisionDto>(
    useCallback(async (data) => {
      data.projectId = projectId;
      await QueryFactory.RevisionClient.create(data);
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.RevisionQuery.searchQueryKey(),
      });
      navigate(Links.Authorized.Projects.link());
    }, []),
  );

  return (
    <ProjectProvider projectId={params.projectId}>
      <Loading loading={form.formState.isSubmitting}>
        <AppLink
          color={ButtonColor.Primary}
          to={Links.Authorized.ProjectDetails.link({ projectId })}
        >
          Back
        </AppLink>
        <Grid container justifyContent={'center'}>
          <Grid item xs={12} md={6} lg={4}>
            <form onSubmit={form.handleSubmitDefault}>
              <Field title={i18n.t('version')}>
                <Input
                  {...form.register('version', { ...requiredRule() })}
                  errorText={form.formState.errors.version?.message}
                />
              </Field>
              <FormError>{form.overallError}</FormError>
              <Button type={'submit'} title={i18n.t('create_button')} />
            </form>
          </Grid>
        </Grid>
      </Loading>
    </ProjectProvider>
  );
};
